"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Users,
  Shield,
  BarChart3,
  User,
  CreditCard,
  LogOut,
  Mail,
  ScrollText,
  Settings,
  ChevronLeft,
  type LucideIcon,
} from "lucide-react";
import { authService } from "@/services/authService";
import { useErrorToast } from "@/components/ui/Toast";
import { usePermissions } from "@/contexts/PermissionContext";
import { Permission } from "@/types/roles";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/types/dictionary";

interface MoreSheetProps {
  mode: "global" | "event";
  lang: Locale;
  dict: Dictionary;
  eventId?: string;
  onClose: () => void;
}

const itemClass =
  "flex items-center gap-3.5 px-3.5 py-3 rounded-xl text-[15px] font-medium text-foreground hover:bg-brand-glow-08 hover:text-brand-light w-full text-left transition-colors";
const iconClass = "w-5 h-5 text-muted-foreground shrink-0";
const labelClass =
  "font-mono text-[10px] tracking-[0.10em] uppercase text-muted px-3.5 pt-2 pb-1";

function SheetLink({
  href,
  icon: Icon,
  label,
  onClose,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  onClose: () => void;
}) {
  return (
    <Link href={href} className={itemClass} onClick={onClose}>
      <Icon className={iconClass} aria-hidden />
      <span>{label}</span>
    </Link>
  );
}

export function MoreSheet({
  mode,
  lang,
  dict,
  eventId,
  onClose,
}: MoreSheetProps) {
  const router = useRouter();
  const showError = useErrorToast();
  const { hasPermission, isSystemAdmin, isCompanyOwner } = usePermissions();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const s = dict?.layout?.sidebar as Record<string, string> | undefined;
  const canSeeTeam = hasPermission(Permission.VIEW_USERS);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authService.logout();
      router.push(`/${lang}/login`);
    } catch (error) {
      console.error("Logout error:", error);
      showError("Çıkış yapılırken bir hata oluştu");
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="lg:hidden">
      <div
        className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-[6px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="menu"
        className="fixed left-0 right-0 bottom-0 z-[70] bg-card border-t border-border rounded-t-[22px] shadow-[0_-20px_60px_-8px_rgba(0,0,0,0.7)] p-2 pb-[calc(20px+env(safe-area-inset-bottom,16px))] flex flex-col animate-[slide-up_240ms_ease-out]"
      >
        <div className="w-9 h-1 bg-bd-hi rounded-full mx-auto my-2" />

        {mode === "global" ? (
          <>
            {canSeeTeam && (
              <div className="flex flex-col">
                <span className={labelClass}>EKİP</span>
                <SheetLink
                  href={`/${lang}/users`}
                  icon={Users}
                  label={s?.users ?? "Kullanıcılar"}
                  onClose={onClose}
                />
                <SheetLink
                  href={`/${lang}/roles`}
                  icon={Shield}
                  label={s?.roles ?? "Roller"}
                  onClose={onClose}
                />
              </div>
            )}

            <div className="flex flex-col border-t border-border mt-1 pt-1">
              <span className={labelClass}>YÖNETİM</span>
              {isSystemAdmin && (
                <SheetLink
                  href={`/${lang}/admin`}
                  icon={BarChart3}
                  label={s?.adminDashboard ?? "Admin"}
                  onClose={onClose}
                />
              )}
              <SheetLink
                href={`/${lang}/profile`}
                icon={User}
                label={s?.profile ?? "Profil"}
                onClose={onClose}
              />
              {(isCompanyOwner || isSystemAdmin) && (
                <SheetLink
                  href={`/${lang}/subscription`}
                  icon={CreditCard}
                  label={s?.subscription ?? "Abonelik"}
                  onClose={onClose}
                />
              )}
            </div>

            <div className="border-t border-border mt-1 pt-1">
              <button
                type="button"
                disabled={isLoggingOut}
                onClick={handleLogout}
                className={cn(
                  itemClass,
                  "text-error-light hover:bg-error-glow-12 hover:text-error-light",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <LogOut
                  className="w-5 h-5 text-error-light shrink-0"
                  aria-hidden
                />
                <span>
                  {isLoggingOut
                    ? ((
                        dict?.layout?.header as
                          | Record<string, string>
                          | undefined
                      )?.loggingOut ?? "Çıkış yapılıyor…")
                    : (s?.logout ?? "Çıkış Yap")}
                </span>
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col">
            <SheetLink
              href={`/${lang}/events/${eventId}/invitations`}
              icon={Mail}
              label={s?.invitations ?? "Davetiyeler"}
              onClose={onClose}
            />
            <SheetLink
              href={`/${lang}/events/${eventId}/log`}
              icon={ScrollText}
              label={s?.log ?? "Log"}
              onClose={onClose}
            />
            <SheetLink
              href={`/${lang}/events/${eventId}/settings`}
              icon={Settings}
              label={s?.settings ?? "Ayarlar"}
              onClose={onClose}
            />
            <div className="border-t border-border mt-1 pt-1">
              <SheetLink
                href={`/${lang}/events`}
                icon={ChevronLeft}
                label={s?.backToEvents ?? "Etkinliklere dön"}
                onClose={onClose}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
